/**
 * ECharts Option Builders — series data extraction.
 *
 * Converts unified ChartData into a normalized SeriesData structure
 * used by all cartesian chart builders (bar, line, area).
 */
import type { ChartData } from '../../../../api/types';
import { formatDate } from '../../../../utils/formatters';
import { colIdx, colType, uniqueValues, formatAxisLabel, isDateColumn } from './utils';

// ── Types ──────────────────────────────────────────────────────────────────

export interface SeriesData {
  categories: string[];
  seriesNames: string[];
  /** seriesName → array of values aligned with categories */
  seriesValues: Map<string, (number | null)[]>;
  isDate: boolean;
  xType: string;
}

// ── Extraction ─────────────────────────────────────────────────────────────

/**
 * Extract categories and series from ChartData.
 * Handles both wide format (multiple y columns) and long format (series column).
 */
export function extractSeriesData(data: ChartData): SeriesData {
  const meta = data.meta;
  const xCol = meta?.x ?? data.columns[0].name;
  const xType = colType(data, xCol);
  const xIdx = colIdx(data, xCol);
  const isDate = isDateColumn(data, xCol);
  const seriesCol = meta?.series;

  if (seriesCol) {
    // Long format: x, seriesCategory, value
    const valueCols = meta?.y ?? [];
    const valueCol = valueCols[0] ?? data.columns[data.columns.length - 1].name;
    const valueIdx = colIdx(data, valueCol);
    const seriesIdx = colIdx(data, seriesCol);

    const categories = uniqueValues(data, xCol);
    const seriesNames = uniqueValues(data, seriesCol).map(String);

    const lookup = new Map<string, Map<any, any>>();
    for (const row of data.rows) {
      const sName = String(row[seriesIdx]);
      const xVal = row[xIdx];
      const yVal = row[valueIdx];
      if (!lookup.has(sName)) lookup.set(sName, new Map());
      lookup.get(sName)!.set(xVal, yVal);
    }

    const seriesValues = new Map<string, (number | null)[]>();
    for (const name of seriesNames) {
      const valueMap = lookup.get(name)!;
      seriesValues.set(name, categories.map((cat) => valueMap.get(cat) ?? null));
    }

    return {
      categories: categories.map((c) => formatAxisLabel(c, xType)),
      seriesNames,
      seriesValues,
      isDate,
      xType,
    };
  }

  // Wide format: first col = x, rest = y series
  const yCols = meta?.y ?? data.columns.slice(1).map((c) => c.name);
  const categories = data.rows.map((row) =>
    isDate ? formatDate(row[xIdx], 'date') : String(row[xIdx] ?? ''),
  );

  const seriesValues = new Map<string, (number | null)[]>();
  for (const yCol of yCols) {
    const yIdx = colIdx(data, yCol);
    seriesValues.set(yCol, data.rows.map((row) => row[yIdx]));
  }

  return {
    categories,
    seriesNames: yCols,
    seriesValues,
    isDate,
    xType,
  };
}